import {TimelineLite} from "gsap";
import * as HLF from "html-living-framework";
import * as springroll from "springroll";
import {Assets} from "./data/Assets";
import {IsSpringrollShell} from "./data/IsSpringrollShell";
import { DefaultCaptionRenderer } from "./gameobjects/DefaultCaptionRenderer";
import {AudioChannel} from "./managers/AudioChannel";
import {CaptionManager} from "./managers/CaptionManager";
import { LightsOutBadgePopup } from "./popups/LightsOutBadgePopup";
import { PlantingBadgePopup } from "./popups/PlantingBadgePopup";
import { TrashBadgePopup } from "./popups/TrashBadgePopup";
import { Intro } from "./states/Intro";
import { LightsOutElmoBathroomScreen } from "./states/LightsOutElmoBathroomScreen";
import { LightsOutElmoBedroomScreen } from "./states/LightsOutElmoBedroomScreen";
import { LightsOutElmoKitchenScreen } from "./states/LightsOutElmoKitchenScreen";
import { LightsOutElmoLivingRoomScreen } from "./states/LightsOutElmoLivingRoomScreen";
import { LightsOutIntroScreen } from "./states/LightsOutIntroScreen";
import {LoadScreen} from "./states/LoadScreen";
import { MainMenu } from "./states/MainMenu";
import { PlanterCustomizeIntroScreen } from "./states/PlanterCustomizeIntroScreen";
import { PlanterCustomizeScreen } from "./states/PlanterCustomizeScreen";
import { PlanterPlantingIntroScreen } from "./states/PlanterPlantingIntroScreen";
import { PlanterPlantingScreen } from "./states/PlanterPlantingScreen";
import { SplashScreen } from "./states/SplashScreen";
import {StartScreen} from "./states/StartScreen";
import { TrashCollectIntroScreen } from "./states/TrashCollectIntroScreen";
import { TrashCollectOutroScreen } from "./states/TrashCollectOutroScreen";
import {TrashCollectScreen} from "./states/TrashCollectScreen";
import { TrashReturnScreen } from "./states/TrashReturnScreen";

import * as CaptionData from "../json/close-caption.json";
import isSesame from "./isSesame";

export class Application extends HLF.Application {
    public startTime: number = 0;
    public playthroughStartTime: number = 0;
    public round: number = 1;
    public playthrough: number = 1;

    static get instance() {
        if (HLF.Application._instance === undefined) {
            HLF.Application._instance = new Application();
        }
        return HLF.Application._instance as Application;
    }

    public get requiredAssets(): HLF.AssetMapData[] {
        return [
            new HLF.FontAsset("helvetica"),
            new HLF.FontAsset("sesame"),
        ];
    }

    public resizeDisabled = false;

    protected _touchManager: HLF.Input.TouchManager;
    protected captionManager?: CaptionManager;

    private _springroll!: springroll.Application;
    private _paused: boolean = false;

    public get paused() { return this._paused; }

    constructor() {
        super({
            transparent: true,
            preserveDrawingBuffer: (
                PIXI.utils.isMobile.android.device ||
                PIXI.utils.isMobile.amazon.device
            ) ? true : undefined,
        });

        this._audioManager.debug = false;
        this._stateManager.debug = false;
        this._loadManager.debug = false;
        this._popupManager.debug = false;

        PIXI.settings.FILTER_RESOLUTION = this.renderer.resolution;

        this._touchManager = new HLF.Input.TouchManager(this.renderer.plugins.interaction, this.stage);
        this._touchManager.onSwipeStart = (pDelta) => {
            PubSub.publishSync("swipestart", pDelta);
        };
        this._touchManager.onSwipe = (pDelta) => {
            PubSub.publishSync("swipe", pDelta);
        };
        this._touchManager.onSwipeEnd = (pDelta) => {
            PubSub.publishSync("swipeend", pDelta);
        };
    }

    public createAssetMap() {
        super.createAssetMap();

        HLF.AssetMap.addAssetGroup(SplashScreen.NAME, Assets.boot);
        HLF.AssetMap.addAssetGroup(Intro.NAME, [...Assets.common, ...Assets.mainmenu]);
        HLF.AssetMap.addAssetGroup(MainMenu.NAME, [...Assets.common, ...Assets.mainmenu]);

        HLF.AssetMap.addAssetGroup(TrashCollectIntroScreen.NAME, [...Assets.common, ...Assets.trashcommon]);
        HLF.AssetMap.addAssetGroup(TrashCollectScreen.NAME, [...Assets.common, ...Assets.trashcommon, ...Assets.trashcollect]);
        HLF.AssetMap.addAssetGroup(TrashCollectOutroScreen.NAME, [...Assets.common, ...Assets.trashcommon]);
        HLF.AssetMap.addAssetGroup(TrashReturnScreen.NAME, [...Assets.common, ...Assets.trashcommon, ...Assets.trashreturn]);

        HLF.AssetMap.addAssetGroup(PlanterCustomizeIntroScreen.NAME, [...Assets.common, ...Assets.plantercommon]);
        HLF.AssetMap.addAssetGroup(PlanterCustomizeScreen.NAME, [...Assets.common, ...Assets.plantercommon, ...Assets.plantercustomize]);
        HLF.AssetMap.addAssetGroup(PlanterPlantingIntroScreen.NAME, [...Assets.common, ...Assets.plantercommon]);
        HLF.AssetMap.addAssetGroup(PlanterPlantingScreen.NAME, [...Assets.common, ...Assets.plantercommon, ...Assets.planterplanting]);

        HLF.AssetMap.addAssetGroup(LightsOutIntroScreen.NAME, [...Assets.common, ...Assets.lightsoutcommon, ...Assets.lightsoutIntro]);
        HLF.AssetMap.addAssetGroup(LightsOutElmoBathroomScreen.NAME, [...Assets.common, ...Assets.lightsoutcommon, ...Assets.bathroom]);
        HLF.AssetMap.addAssetGroup(LightsOutElmoBedroomScreen.NAME, [...Assets.common, ...Assets.lightsoutcommon, ...Assets.bedroom]);
        HLF.AssetMap.addAssetGroup(LightsOutElmoKitchenScreen.NAME, [...Assets.common, ...Assets.lightsoutcommon, ...Assets.kitchen]);
        HLF.AssetMap.addAssetGroup(LightsOutElmoLivingRoomScreen.NAME, [...Assets.common, ...Assets.lightsoutcommon, ...Assets.livingroom]);
    }

    public createSplashScreen() {
        return new SplashScreen();
    }

    public registerStates() {
        super.registerStates();

        this._stateManager.registerState(Intro.NAME, () => new Intro());

        this._stateManager.registerState(MainMenu.NAME, () => new MainMenu());

        this._stateManager.registerState(TrashCollectIntroScreen.NAME, () => new TrashCollectIntroScreen());
        this._stateManager.registerState(TrashCollectScreen.NAME, () => new TrashCollectScreen());
        this._stateManager.registerState(TrashCollectOutroScreen.NAME, () => new TrashCollectOutroScreen());
        this._stateManager.registerState(TrashReturnScreen.NAME, () => new TrashReturnScreen());

        this._stateManager.registerState(PlanterCustomizeIntroScreen.NAME, () => new PlanterCustomizeIntroScreen());
        this._stateManager.registerState(PlanterCustomizeScreen.NAME, () => new PlanterCustomizeScreen());
        this._stateManager.registerState(PlanterPlantingIntroScreen.NAME, () => new PlanterPlantingIntroScreen());
        this._stateManager.registerState(PlanterPlantingScreen.NAME, () => new PlanterPlantingScreen());

        this._stateManager.registerState(LightsOutIntroScreen.NAME, () => new LightsOutIntroScreen());
        this._stateManager.registerState(LightsOutElmoBathroomScreen.NAME, () => new LightsOutElmoBathroomScreen());
        this._stateManager.registerState(LightsOutElmoBedroomScreen.NAME, () => new LightsOutElmoBedroomScreen());
        this._stateManager.registerState(LightsOutElmoKitchenScreen.NAME, () => new LightsOutElmoKitchenScreen());
        this._stateManager.registerState(LightsOutElmoLivingRoomScreen.NAME, () => new LightsOutElmoLivingRoomScreen());
    }

    public registerLoadingScreens() {
        super.registerLoadingScreens();

        this._loadManager.registerLoadScreen(StartScreen.NAME, () => new StartScreen());
        this._loadManager.registerLoadScreen(LoadScreen.NAME, () => new LoadScreen());
    }

    public registerPopups() {
        super.registerPopups();

        this._popupManager.registerPopup(() => new TrashBadgePopup(), TrashBadgePopup.NAME);
        this._popupManager.registerPopup(() => new LightsOutBadgePopup(), LightsOutBadgePopup.NAME);
        this._popupManager.registerPopup(() => new PlantingBadgePopup(), PlantingBadgePopup.NAME);
    }

    public update() {
        super.update();
        const deltaTime: number = PIXI.Ticker.shared.elapsedMS / 1000;
        if (!this.paused) {
            this.captionManager?.update(deltaTime);
        }
    }

    public init() {
        super.init();

        this.initSpringroll();

        this.resizer.sizeMin = new PIXI.Point(1024, 768);
        this.resizer.sizeMax = new PIXI.Point(1664, 768);

        this._webEventsManager.registerVisibilityChangedCallback((pVisible) => {
            this.onPause(!pVisible, "document.visibilitychange");
        });
        if (isSesame) {
            window.addEventListener("blur", () => this.onPause(true, "window.blur"));
            window.addEventListener("focus", () => this.onPause(false, "window.focus"));
        }
    }

    protected onResize(pDelay?: number) {
        if (!this.resizeDisabled) {
            super.onResize(pDelay);
        }
        new TimelineLite().call(() => {
            this.captionManager?.onResize(this.resizer.getSize());
        }, undefined, this, pDelay);
    }

    protected onLoadRequiredAssetsComplete() {
        super.onLoadRequiredAssetsComplete();
        this.captionManager = new CaptionManager((CaptionData as any).default);
        this.stage.addChild(this.captionManager);
        this.captionManager.setRenderer(new DefaultCaptionRenderer("sesame", 46 * HLF.Application.instance.renderer.resolution));
        this.captionManager.onResize(this._size);
        this.captionManager.active = !this._springroll.state.captionsMuted.value;

        this._voiceoverManager.debug = false;

        PubSub.publishSync(
            HLF.STATE_LOAD_STATE,
            new HLF.StateToken({id: Intro.NAME, data: {}},
                StartScreen.NAME,
                HLF.TransitionStep.HideLoadScreen,
                HLF.TransitionStep.ShowLoadScreen,
                ...HLF.StateToken.TRANSITION_FIRST_VIEW),
        );
        PubSub.publish(HLF.PUSH_KEYBOARD_LAYER, undefined);

        AudioChannel.BGM.volume = 0.075;

        const interactionManager: PIXI.InteractionManager = this.renderer.plugins.interaction!;
        interactionManager.cursorStyles.default = "url('images/static/starcursor_default.png') 16 16, auto";
        interactionManager.cursorStyles.pointer = "url('images/static/starcursor_rolloverstate_final.png') 16 16, auto";
    }

    private initSpringroll(): void {
        this._springroll = new springroll.Application({
            features: {
                captions: true,
                sound: true,
                vo: true,
                music: true,
                sfx: true,
            },
        });

        this._springroll.state.ready.subscribe(() => {
            this.onSpringrollInitialized();
        });

        this._springroll.state.pause.subscribe((isPaused: boolean) => {
            this.onPause(isPaused, "springroll");
        });

        this._springroll.state.playOptions.subscribe((playOptions: object) => {
            console.log("New playOptions value set to", playOptions);
        });

        this._springroll.state.captionsMuted.subscribe((result: boolean) => {
            this.onCaptionsMuted(result);
        });

        this._springroll.state.soundVolume.subscribe((result: number) => {
            this.onSoundVolume(result);
        });

        this._springroll.state.musicVolume.subscribe((result: number) => {
            this.onMusicVolume(result);
        });

        this._springroll.state.voVolume.subscribe((result: number) => {
            this.onVOVolume(result);
        });

        this._springroll.state.sfxVolume.subscribe((result: number) => {
            this.onSFXVolume(result);
        });
    }

    private onSpringrollInitialized(): void {
        // override
    }

    // Springroll message listeners
    private onCaptionsMuted(pMuted: boolean): void {
        if (IsSpringrollShell() && this.captionManager) {
            this.captionManager.active = !pMuted;
        }
    }

    private onSoundVolume(pResults: number): void {
        AudioChannel.mute = pResults === 0;
        this.audio.masterVolume = pResults;
    }

    private onVOVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.VO, pResults);
    }

    private onMusicVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.BGM, pResults === 0 ? 0 : 0.075);
    }

    private onSFXVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.SFX, pResults);
    }

    private onPause(pIsPaused: boolean, pReason?: string): void {
        const wasPaused = this._paused;
        if (pIsPaused && !wasPaused) {
            console.log("paused", pReason);
            PubSub.publishSync(HLF.GAME_PAUSED, undefined);
        } else if (wasPaused && !pIsPaused) {
            console.log("unpaused", pReason);
            PubSub.publishSync(HLF.GAME_UNPAUSED, undefined);
        }
        else {
            console.warn("already " + (pIsPaused ? "paused" : "unpaused"), pReason);
        }
        AudioChannel.mute = pIsPaused;
        this._paused = pIsPaused;
        console.log("audio is " + (AudioChannel.mute ? "muted" : "unmuted"));
    }
}
